import { signal, computed } from "@preact/signals";
import { formatMoney, cents, type PrintRequested, type EventEnvelope } from "@orderman/shared";
import { adminEvents } from "../../state/adminData.js";
import { requestReprint } from "../../state/store.js";

// Bons: die ECHTEN an die Stationen gesendeten Bons/Belege (aus print.requested projiziert),
// mit Nachdruck. Mobile Karten statt der Portal-Tabelle.

const PRINTERS: Record<string, string> = { all: "Alle", kitchen: "Küche", bar: "Bar", receipt: "Beleg" };
const printerFilter = signal<string>("all");
const reprintBusy = signal<string | null>(null);
const flash = signal<string | null>(null);

interface BonRow {
  ev: PrintRequested;
  nr: number | undefined;
  seqNo: number | undefined;
  tableRef: string;
  station: string;
  pos: number;
  amountCents: number;
  atMs: number;
}

const rows = computed<BonRow[]>(() => {
  const list: BonRow[] = [];
  for (const e of (adminEvents.value ?? []) as EventEnvelope[]) {
    if (e.body.kind !== "print.requested") continue;
    const ev = e.body as PrintRequested;
    const doc = ev.doc;
    list.push({
      ev,
      nr: doc.kind === "kitchen" ? doc.bonNo : doc.belegNo,
      seqNo: doc.seqNo,
      tableRef: doc.tableRef,
      station: PRINTERS[ev.printer] ?? ev.printer,
      pos: doc.kind === "kitchen" ? doc.lines.reduce((s, l) => s + l.qty, 0) : doc.items.reduce((s, i) => s + i.qty, 0),
      amountCents: doc.kind === "receipt" ? doc.totalCents : 0,
      atMs: ev.atMs,
    });
  }
  return list
    .filter((b) => printerFilter.value === "all" || b.ev.printer === printerFilter.value)
    .sort((a, b) => b.atMs - a.atMs);
});

function clock(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function reprint(ev: PrintRequested): void {
  reprintBusy.value = ev.printJobId;
  requestReprint(ev);
  flash.value = `Nachdruck an „${PRINTERS[ev.printer] ?? ev.printer}" ausgelöst.`;
  setTimeout(() => {
    reprintBusy.value = null;
    flash.value = null;
  }, 2500);
}

export function BonsView() {
  const list = rows.value;
  return (
    <div class="admin-scroll">
      <div class="bon-filter">
        {Object.entries(PRINTERS).map(([key, lbl]) => (
          <button
            key={key}
            class={printerFilter.value === key ? "seg-btn on" : "seg-btn"}
            onClick={() => (printerFilter.value = key)}
          >
            {lbl}
          </button>
        ))}
      </div>

      {flash.value && <div class="admin-flash">{flash.value}</div>}

      {list.length === 0 ? (
        <div class="admin-empty">Noch keine gedruckten Bons.</div>
      ) : (
        list.map((b) => (
          <div key={b.ev.printJobId} class="bon-card">
            <div class="bon-head">
              <span class="bon-nr">
                {b.nr ? `#${b.nr}` : "•"}
                {b.ev.reprintOf && <span class="bon-badge">ND</span>}
              </span>
              <span class="bon-station">{b.station}</span>
              <span class="bon-table">Tisch {b.tableRef}</span>
              <span class="bon-time">{clock(b.atMs)}</span>
            </div>
            <div class="bon-foot">
              <span>{b.pos} Pos.{b.seqNo ? ` · Best. #${b.seqNo}` : ""}</span>
              {b.amountCents > 0 && <span class="bon-amount">{formatMoney(cents(b.amountCents))}</span>}
              <button class="bon-reprint" disabled={reprintBusy.value === b.ev.printJobId} onClick={() => reprint(b.ev)}>
                {reprintBusy.value === b.ev.printJobId ? "…" : "🖨 Nachdruck"}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
